/**
 * Versioned Envelope Detection
 *
 * Detects whether a parsed JSON value is in the versioned envelope format
 * `{ version: number, value: T }`. Ported near-verbatim from
 * `ersatzteil-scraper` `src/lib/versioned/envelope.ts` (relative imports only
 * — ADR-0005).
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 */

import { z } from 'zod';

/**
 * Canonical Zod schema for the Versioned-Envelope shape. Plugin versions are
 * 1-indexed positive integers, so `version: 0` / `1.5` / negatives are
 * rejected up-front rather than failing later in the schema-Map lookup.
 */
export const versionedEnvelopeSchema = z.object({
  value: z.unknown(),
  version: z.number().int().positive(),
});

/**
 * Type guard narrowing `unknown` to `{ version: number; value: unknown }`.
 *
 * Loose form (accepts `0` / `NaN` / non-integers); prefer
 * `versionedEnvelopeSchema.safeParse(...)` for new disk-backed readers. Kept
 * for the serializer's in-memory round-trip path.
 */
export function isVersionedEnvelope(
  parsed: unknown
): parsed is { value: unknown; version: number } {
  return (
    typeof parsed === 'object' &&
    parsed !== null &&
    !Array.isArray(parsed) &&
    'version' in parsed &&
    'value' in parsed &&
    typeof (parsed as Record<string, unknown>).version === 'number'
  );
}
